import * as React from "react";

import { cn } from "@/lib/utils";

type ProgressProps = React.HTMLAttributes<HTMLDivElement> & {
  value?: number | null;
  max?: number;
  leftLabel?: string;
  rightLabel?: string;
};

function clampProgress(value?: number | null, max = 100) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }

  if (max <= 0 || Number.isNaN(max)) {
    return 0;
  }

  return Math.min(Math.max(value, 0), max);
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    { className, value = 0, max = 100, leftLabel, rightLabel, ...props },
    ref,
  ) => {
    const safeValue = clampProgress(value, max);
    const percent = (safeValue / max) * 100;

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={safeValue}
        className={cn(
          "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
          className,
        )}
        {...props}
      >
        <div
          className="h-full w-full bg-primary transition-all"
          style={{ transform: `translateX(-${100 - percent}%)` }}
        />
        {leftLabel && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "0.5rem",
              transform: "translateY(-50%)",
              fontSize: "0.75rem",
              lineHeight: "1rem",
              color: "white",
              zIndex: 10,
              pointerEvents: "none",
              fontWeight: "bold",
            }}
          >
            {leftLabel}
          </div>
        )}
        {rightLabel && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: "0.5rem",
              transform: "translateY(-50%)",
              fontSize: "0.75rem",
              lineHeight: "1rem",
              color: "black",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            {rightLabel}
          </div>
        )}
      </div>
    );
  },
);

Progress.displayName = "Progress";

export { Progress };
